import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Boxes, Home, LogOut, PackageOpen, UserRound } from 'lucide-react'
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { api, queryKeys } from '../lib/api'
import { LoadingState, Toast } from '../components/ui'
import { LoginPage } from '../pages/LoginPage'
import { DashboardPage } from '../pages/DashboardPage'
import { InventoryPage } from '../pages/InventoryPage'
import { ProjectsPage } from '../pages/ProjectsPage'
import { LedgerPage } from '../pages/LedgerPage'
import { ProfilePage } from '../pages/ProfilePage'

export const APP_VERSION = 'v4.0.0'

const navItems = [
  { to: '/', label: '首页', icon: Home, end: true },
  { to: '/inventory', label: '库存', icon: Boxes },
  { to: '/projects', label: '作品', icon: PackageOpen },
  { to: '/ledger', label: '账本', icon: BookOpen },
  { to: '/profile', label: '维护', icon: UserRound },
]

function AppShell() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api<
      | { authenticated: true; absoluteExpiresAt: string }
      | { authenticated: false }
    >('/api/auth/session'),
    retry: false,
    staleTime: 60_000,
  })

  useEffect(() => {
    const unauthorized = () => {
      queryClient.clear()
      navigate('/login', { replace: true, state: { from: location.pathname } })
    }
    window.addEventListener('bead:unauthorized', unauthorized)
    return () => window.removeEventListener('bead:unauthorized', unauthorized)
  }, [location.pathname, navigate, queryClient])

  if (session.isPending) return <main className="boot"><div className="brand-mark"><span /><span /><span /><span /></div><LoadingState label="正在安全打开你的拼豆库…" /></main>
  if (session.isError) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  if (session.data.authenticated === false) return <Navigate to="/login" replace state={{ from: location.pathname }} />

  return <div className="app-shell">
    <aside className="sidebar">
      <NavLink to="/" className="brand" aria-label="拼豆库存首页">
        <span className="brand-mark"><span /><span /><span /><span /></span>
        <span><strong>拼豆库存</strong><small>手作工作台</small></span>
      </NavLink>
      <nav aria-label="主导航">{navItems.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={Boolean(end)} className={({ isActive }) => isActive ? 'nav-link nav-link--active' : 'nav-link'}><Icon aria-hidden="true" /><span>{label}</span></NavLink>)}</nav>
      <footer><span>{APP_VERSION}</span><span>数据仅你可见</span></footer>
    </aside>
    <main className="main-content"><Outlet /></main>
    <nav className="bottom-nav" aria-label="主导航">{navItems.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={Boolean(end)} className={({ isActive }) => isActive ? 'bottom-nav__link bottom-nav__link--active' : 'bottom-nav__link'}><Icon aria-hidden="true" /><span>{label}</span></NavLink>)}</nav>
    <Toast />
  </div>
}

function NotFound() {
  return <div className="not-found"><span>404</span><h1>这一页还没有装进拼豆盒</h1><p>路径不存在，可以回到首页继续。</p><NavLink className="button button--primary" to="/">回到首页</NavLink></div>
}

export function App() {
  return <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route element={<AppShell />}>
      <Route index element={<DashboardPage />} />
      <Route path="inventory" element={<InventoryPage />} />
      <Route path="projects" element={<ProjectsPage />} />
      <Route path="ledger" element={<LedgerPage />} />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
}

export async function logout(): Promise<void> {
  await api('/api/auth/logout', { method: 'POST', body: '{}' })
  window.location.assign('/login')
}

export { LogOut }
